/**
 * Florida service-area city pages (GEO landing pages). Facts here are kept to
 * well-known, general climatology and public utility territories — no invented
 * addresses, ratings or statistics. Seasonal notes describe typical patterns.
 */
export const CITIES = [
  {
    slug: "miami",
    name: "Miami",
    county: "Miami-Dade County",
    utilities: "Florida Power & Light (FPL) serves most of Miami-Dade, with some municipal gas and electric service in city pockets.",
    blurb:
      "Miami's long dry season and strong year-round sun make commercial rooftops unusually productive, while afternoon summer thunderstorms and hurricane season (June through November) are why wind-rated racking and permitting matter here. Warehouses, retail plazas and mid-rise offices across Miami-Dade carry large, mostly unshaded flat roofs — exactly the profile where a bill-based review tends to find real headroom.",
    mapQuery: "Miami, FL",
    lat: 25.7617,
    lng: -80.1918,
    seasons: [
      ["Winter (Dec–Feb)", "Dry, mild and sunny — strong production with lower air-conditioning loads."],
      ["Spring (Mar–May)", "The driest, clearest stretch of the Miami year; excellent solar months."],
      ["Summer (Jun–Sep)", "Hot and humid with near-daily afternoon storms; AC peaks push demand charges up."],
      ["Fall (Oct–Nov)", "Hurricane season tapers, rain thins out, and sun stays strong through November."],
    ],
    faqs: [
      ["Does Miami get enough sun for commercial solar?", "Yes — South Florida is one of the sunnier large metro areas in the country, and the long dry season keeps winter production strong. The realistic review comes from your bills and a roof look, not from a statewide average."],
      ["Which utility serves Miami businesses?", "FPL serves most of Miami-Dade, so bills, rate schedules and interconnection run through FPL. Some pockets have municipal service — your bill's header will say which."],
      ["What about hurricanes?", "Miami-Dade has some of the strictest wind-rating and permitting rules in the state. That is a design input, not a dealbreaker — systems are engineered for it."],
    ],
  },
  {
    slug: "tampa",
    name: "Tampa",
    county: "Hillsborough County",
    utilities: "Tampa Electric (TECO) serves most of the immediate Tampa area; Duke Energy covers much of the surrounding region.",
    blurb:
      "The Tampa Bay area sits in one of the lightning capitals of North America, which says two things at once: enormous summer convection, and a very real case for storage and surge-conscious design. Between the port logistics, distribution and healthcare buildings around the bay, Tampa has the flat, big-footprint roofs that commercial solar uses best.",
    mapQuery: "Tampa, FL",
    lat: 27.9506,
    lng: -82.4572,
    seasons: [
      ["Winter (Dec–Feb)", "Mild and dry by Florida standards; solid production without heavy AC load."],
      ["Spring (Mar–May)", "Dry and sunny with building heat — the strongest production-to-savings stretch."],
      ["Summer (Jun–Sep)", "Peak heat, peak humidity and near-daily lightning-bearing storms; AC demand spikes."],
      ["Fall (Oct–Nov)", "Storms ease, humidity drops, and hurricane season winds down through November."],
    ],
    faqs: [
      ["Is the lightning a problem for rooftop solar?", "The area's summer storms are frequent but systems are designed for it: grounding, surge protection and wind-rated racking are standard parts of a Florida commercial design, not extras."],
      ["Which utility serves Tampa businesses?", "Tampa Electric (TECO) serves most of the city, with Duke Energy in much of the wider region. Your bill identifies your exact tariff."],
      ["Can batteries help in Tampa?", "Often, yes — buildings with spiky afternoons and demand charges tend to see the clearest storage scenarios. Interval data from your utility settles it."],
    ],
  },
  {
    slug: "orlando",
    name: "Orlando",
    county: "Orange County",
    utilities: "Duke Energy serves much of the Orlando area; the municipal utility OUC and several others cover parts of the metro.",
    blurb:
      "Orlando's inland position means hot, still summers and a tourism-and-hospitality building stock — hotels, kitchens, attractions and the distribution centers that serve them — with long operating hours that match solar production well. Convention traffic runs on daytime power, which is exactly when panels produce.",
    mapQuery: "Orlando, FL",
    lat: 28.5383,
    lng: -81.3792,
    seasons: [
      ["Winter (Dec–Feb)", "Cool mornings, mild days, and the driest stretch of the year."],
      ["Spring (Mar–May)", "Sunny and increasingly hot; the year's best solar window before the wet season."],
      ["Summer (Jun–Sep)", "Hot, humid afternoons with thunderstorms; cooling loads dominate the bill."],
      ["Fall (Oct–Nov)", "Wet season exits; clear, warm days return through November."],
    ],
    faqs: [
      ["Does Orlando have good solar production?", "Central Florida's year-round sun supports solid commercial production; the honest number comes from your roof, your bills and a real layout."],
      ["Which utility serves Orlando businesses?", "Duke Energy serves much of the metro, OUC serves parts of the city, and a few other municipal utilities cover the edges — the bill header names yours."],
      ["Do hotels and kitchens benefit differently?", "Buildings with long daytime operating hours line up well with solar production; demand charges from kitchen and cooling equipment are where storage scenarios come in."],
    ],
  },
  {
    slug: "jacksonville",
    name: "Jacksonville",
    county: "Duval County",
    utilities: "JEA, the municipal utility, serves much of Jacksonville, with FPL in parts of the metro.",
    blurb:
      "Jacksonville is Florida's largest city by land area, with an outsized share of warehouses, port-linked logistics and manufacturing floors. Its slightly cooler winters give large flat roofs a comfortable operating profile, and long summer days keep production high when cooling loads peak.",
    mapQuery: "Jacksonville, FL",
    lat: 30.3322,
    lng: -81.6557,
    seasons: [
      ["Winter (Dec–Feb)", "The coolest winters in peninsular Florida — brief cold snaps, mostly sunny."],
      ["Spring (Mar–May)", "Dry, bright and warming; strong production months."],
      ["Summer (Jun–Sep)", "Hot and humid with afternoon storms; long days support peak production."],
      ["Fall (Oct–Nov)", "Warm, drier and quieter as hurricane season fades."],
    ],
    faqs: [
      ["Which utility serves Jacksonville businesses?", "JEA, the city's municipal utility, serves much of Jacksonville; FPL covers parts of the metro. Your bill names the tariff either way."],
      ["Is solar still worthwhile that far north in Florida?", "Yes — Jacksonville's sun and long summer days support solid production; the economics come from your bills, not a latitude rule of thumb."],
      ["What buildings fit best?", "Large, flat, unshaded roofs — warehouses, distribution and manufacturing — typically offer the most usable area per dollar."],
    ],
  },
  {
    slug: "fort-lauderdale",
    name: "Fort Lauderdale",
    county: "Broward County",
    utilities: "Florida Power & Light (FPL) serves Fort Lauderdale and most of Broward County.",
    blurb:
      "Fort Lauderdale's marine economy — marinas, yachting, hospitality and the port — pairs with a dense strip of retail and office along the corridors. Long dry-season stretches and strong coastal sun keep commercial rooftops productive, while salt air and storm codes shape the equipment and engineering choices.",
    mapQuery: "Fort Lauderdale, FL",
    lat: 26.1224,
    lng: -80.1373,
    seasons: [
      ["Winter (Dec–Feb)", "Dry, sunny and mild — one of the pleasant production stretches of the year."],
      ["Spring (Mar–May)", "The driest, clearest months; strong output with moderate loads."],
      ["Summer (Jun–Sep)", "Hot and humid with afternoon storms; hurricane season runs through November."],
      ["Fall (Oct–Nov)", "Rain and storm risk taper; sun remains strong."],
    ],
    faqs: [
      ["Does salt air affect solar equipment?", "Coastal installations specify corrosion-resistant racking and equipment rated for marine environments — a normal part of a Broward design, and a question worth asking any installer."],
      ["Which utility serves Fort Lauderdale businesses?", "FPL serves Fort Lauderdale and most of Broward County."],
      ["What about hurricane season?", "Systems are engineered to Florida's wind codes, and Broward's permitting is among the strictest — that is a design consideration, not an obstacle."],
    ],
  },
  {
    slug: "st-petersburg",
    name: "St. Petersburg",
    county: "Pinellas County",
    utilities: "Duke Energy serves most of St. Petersburg; a small municipal area exists downtown.",
    blurb:
      "St. Petersburg bills itself as the Sunshine City, and its coastal peninsula geography means water on three sides, low-rise commercial stock and plenty of open roof. Marinas, healthcare, hospitality and a growing craft-industrial corridor give Pinellas businesses both the roofs and the operating hours that make solar pencil out.",
    mapQuery: "St. Petersburg, FL",
    lat: 27.7676,
    lng: -82.6403,
    seasons: [
      ["Winter (Dec–Feb)", "Mild, dry and famously sunny on the peninsula."],
      ["Spring (Mar–May)", "Dry and bright with building warmth; prime production months."],
      ["Summer (Jun–Sep)", "Hot, humid, storm-prone afternoons; cooling loads spike the bill."],
      ["Fall (Oct–Nov)", "Humidity breaks and the wet season exits."],
    ],
    faqs: [
      ["Is St. Petersburg really that sunny?", "The Sunshine City nickname is earned — the area's dry-season stretch is one of Florida's most reliable. The number that matters is your roof's, which a bill-based review estimates honestly."],
      ["Which utility serves St. Petersburg businesses?", "Duke Energy serves most of the city, with a small municipal utility area downtown. Your bill identifies your provider."],
      ["Can batteries help with afternoon peaks?", "Where demand charges track hot afternoons, storage scenarios are worth studying — your utility's interval data confirms it."],
    ],
  },
  {
    slug: "sarasota",
    name: "Sarasota",
    county: "Sarasota County",
    utilities: "Florida Power & Light (FPL) serves Sarasota and much of the county.",
    blurb:
      "Sarasota's mix of hospitality, healthcare, arts and professional services comes with low-rise commercial buildings and generous roof planes. Southwest Florida's sun is strong year-round, and the seasonal population swing means summer bills — and the savings scenarios around them — look different from winter ones.",
    mapQuery: "Sarasota, FL",
    lat: 27.3364,
    lng: -82.5307,
    seasons: [
      ["Winter (Dec–Feb)", "Dry, sunny and mild — peak season for visitors and steady production."],
      ["Spring (Mar–May)", "The driest stretch; strong sun with moderate cooling loads."],
      ["Summer (Jun–Sep)", "Hot and storm-prone; AC pushes demand charges to their yearly high."],
      ["Fall (Oct–Nov)", "Wet season exits; hurricane risk fades through November."],
    ],
    faqs: [
      ["Which utility serves Sarasota businesses?", "FPL serves Sarasota and much of the surrounding county."],
      ["Does seasonal population affect the numbers?", "Yes — hospitality-driven buildings often see big summer-to-winter bill swings, which is why a review uses twelve months of bills rather than one."],
      ["Are coastal roofs workable?", "With corrosion-rated equipment and wind-code engineering, absolutely — Sarasota installs are designed for the coast."],
    ],
  },
  {
    slug: "fort-myers",
    name: "Fort Myers",
    county: "Lee County",
    utilities: "Florida Power & Light (FPL) serves most of the Fort Myers area; Lee County Electric Cooperative covers parts of the region.",
    blurb:
      "Fort Myers and the surrounding Gulf Coast communities run on hospitality, healthcare, retirement services and fast-growing retail. Long sunny seasons and sprawling single-story commercial architecture give Lee County businesses big usable roofs, while hurricane history makes wind-rated engineering the local baseline, not an upgrade.",
    mapQuery: "Fort Myers, FL",
    lat: 26.6406,
    lng: -81.8723,
    seasons: [
      ["Winter (Dec–Feb)", "Dry, sunny and mild; the pleasant production stretch."],
      ["Spring (Mar–May)", "Driest and clearest months; strong output."],
      ["Summer (Jun–Sep)", "Hot, humid afternoons with storms; peak AC demand."],
      ["Fall (Oct–Nov)", "Storms and humidity taper through November."],
    ],
    faqs: [
      ["Which utility serves Fort Myers businesses?", "FPL serves most of the area, with Lee County Electric Cooperative in parts of the region — your bill names it."],
      ["How does hurricane history affect a project?", "Southwest Florida's building codes already assume storm exposure; solar designs there are wind-rated accordingly."],
      ["What roof types work best locally?", "The region's single-story retail and warehouse stock offers large, open roof planes that suit commercial arrays."],
    ],
  },
  {
    slug: "naples",
    name: "Naples",
    county: "Collier County",
    utilities: "Florida Power & Light (FPL) serves Naples and most of Collier County.",
    blurb:
      "Naples' commercial base — resorts, golf clubs, medical offices and high-end retail — runs hard during the winter season and moderately the rest of the year. The area's sunshine is among the strongest in peninsular Florida, and large flat hospitality roofs offer the square footage that makes commercial arrays worthwhile.",
    mapQuery: "Naples, FL",
    lat: 26.142,
    lng: -81.7948,
    seasons: [
      ["Winter (Dec–Feb)", "Peak visitor season: dry, sunny and mild, with strong production."],
      ["Spring (Mar–May)", "Driest months; excellent sun before the wet season builds."],
      ["Summer (Jun–Sep)", "Hot, humid and storm-prone; quieter season for visitors, heavy for AC."],
      ["Fall (Oct–Nov)", "Rain and storm risk ease; sun stays strong."],
    ],
    faqs: [
      ["Which utility serves Naples businesses?", "FPL serves Naples and most of Collier County."],
      ["Do seasonal businesses still benefit?", "Yes — the fit depends on when you use power, not just how much. A twelve-month bill review matches production to your actual pattern."],
      ["Is the coastal environment a concern?", "Corrosion-rated racking and marine-spec equipment are standard practice for coastal Collier County projects."],
    ],
  },
  {
    slug: "west-palm-beach",
    name: "West Palm Beach",
    county: "Palm Beach County",
    utilities: "Florida Power & Light (FPL), headquartered in the area, serves West Palm Beach and most of the county.",
    blurb:
      "West Palm Beach and the Palm Beaches combine hospitality, government, medical and retail corridors along a coastal strip with strong, steady sun. FPL's home turf is one of the most solar-active utility territories in the state, and the region's flat commercial roofs — from retail plazas to civic buildings — offer ample usable area.",
    mapQuery: "West Palm Beach, FL",
    lat: 26.7153,
    lng: -80.0534,
    seasons: [
      ["Winter (Dec–Feb)", "Dry, sunny and mild; strong production in peak season."],
      ["Spring (Mar–May)", "The clearest, driest stretch of the year."],
      ["Summer (Jun–Sep)", "Hot and humid with afternoon storms; hurricane season continues."],
      ["Fall (Oct–Nov)", "Conditions ease; sun remains strong through the fall."],
    ],
    faqs: [
      ["Which utility serves West Palm Beach businesses?", "FPL serves West Palm Beach and most of Palm Beach County."],
      ["Is this a good area for commercial solar?", "Strong year-round sun, flat roofs and a large utility territory with active solar programs make the Palm Beaches a natural fit — subject, always, to your bills and roof."],
      ["What about storms?", "South Florida wind codes apply; systems are engineered and permitted for hurricane exposure."],
    ],
  },
  {
    slug: "melbourne",
    name: "Melbourne",
    county: "Brevard County",
    utilities: "Florida Power & Light (FPL) serves most of the Melbourne / Space Coast area.",
    blurb:
      "Melbourne and the Space Coast bring aerospace, technology and light manufacturing to Florida's Atlantic shore. Technical facilities run steady, climate-controlled loads all day — a strong match for daytime solar production — and the region's coastal sun keeps flat-topped industrial roofs productive year-round.",
    mapQuery: "Melbourne, FL",
    lat: 28.0836,
    lng: -80.6081,
    seasons: [
      ["Winter (Dec–Feb)", "Mild and dry on the coast; steady production."],
      ["Spring (Mar–May)", "Sunny and dry with rising warmth; prime production months."],
      ["Summer (Jun–Sep)", "Hot, humid afternoons with storms; coastal breeze softens the heat."],
      ["Fall (Oct–Nov)", "Drier and calmer as hurricane season winds down."],
    ],
    faqs: [
      ["Which utility serves Melbourne businesses?", "FPL serves most of the Melbourne and Space Coast area."],
      ["Do technical facilities fit solar well?", "Facilities with steady daytime loads — labs, manufacturing, data rooms — often match solar production closely, which favors the economics."],
      ["Is coastal corrosion an issue?", "Marine-rated equipment is standard for Atlantic-coast installations in Brevard County."],
    ],
  },
  {
    slug: "daytona-beach",
    name: "Daytona Beach",
    county: "Volusia County",
    utilities: "Florida Power & Light (FPL) serves most of the Daytona Beach area, with municipal utilities in parts of Volusia County.",
    blurb:
      "Daytona Beach's hospitality, event and logistics economy sits along one of Florida's most recognizable coastlines. Hotels, venues and distribution buildings put plenty of roof area to work, and the Atlantic-side sun — slightly softer than the Gulf Coast in summer, excellent in the dry season — supports a solid production profile.",
    mapQuery: "Daytona Beach, FL",
    lat: 29.2108,
    lng: -81.0228,
    seasons: [
      ["Winter (Dec–Feb)", "Mild, dry and sunny; event season with steady production."],
      ["Spring (Mar–May)", "Dry and bright; one of the year's best solar stretches."],
      ["Summer (Jun–Sep)", "Hot and humid with afternoon storms and peak AC loads."],
      ["Fall (Oct–Nov)", "Humidity drops; hurricane season fades through November."],
    ],
    faqs: [
      ["Which utility serves Daytona Beach businesses?", "FPL serves most of the area; some Volusia County communities have municipal utilities. Your bill header names yours."],
      ["Is the beach environment hard on equipment?", "Coastal installations use corrosion-rated racking and equipment — standard specification for Atlantic-side projects."],
      ["Do event-driven businesses benefit?", "Yes, with a caveat: production is steady but your usage may spike seasonally, which is exactly why a twelve-month bill review beats a one-bill estimate."],
    ],
  },
];

export function findCity(slug) {
  return CITIES.find((c) => c.slug === slug) || null;
}
